/**
 * EnquiryForm — the photobooth enquiry submission surface.
 *
 * Per Craig's iter217 booking decision (1a-modified): NO automatic
 * payment. The flow is:
 *
 *   1. Customer fills in name + email + phone + event date + venue
 *      + event type + package + (optional) add-ons + notes.
 *   2. POST /api/services/photobooth/enquiry.
 *   3. Admin receives email; customer receives calm acknowledgement.
 *   4. Admin confirms availability manually, then sends a deposit
 *      link separately (Stripe support to land in a future iter).
 *
 * The form lives inside a modal triggered by every "Check
 * Availability" / "Request a Quote" / "Enquire Now" CTA on the
 * photobooth surfaces. Pre-fills `package` when a specific package
 * card was clicked.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  SERIF, IVORY, IVORY_DIM, IVORY_GHOST, GOLD, GOLD_FAINT,
  WALNUT_CARD, PILL_BUTTON_PRIMARY,
} from './_tokens';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const inputBase = {
  width: '100%', padding: '13px 16px',
  borderRadius: 10,
  background: 'rgba(20,12,6,0.78)',
  border: `1px solid ${GOLD_FAINT}`,
  color: IVORY, fontFamily: SERIF, fontStyle: 'italic',
  fontSize: 14.5, outline: 'none',
  boxShadow: 'inset 0 1px 0 rgba(212,170,82,0.06)',
};

const labelStyle = {
  display: 'block',
  fontFamily: SERIF, fontStyle: 'italic',
  fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
  color: IVORY_GHOST, marginBottom: 6,
};

const EVENT_TYPES = [
  'Wedding', 'Prom', 'Birthday', 'Corporate', 'Anniversary',
  'Christening', 'Engagement', 'Charity / Gala', 'Other',
];

const EnquiryForm = ({
  packages = [],
  addons = [],
  initialPackageSlug = '',
  initialEventDate = '',
  onClose,
  sourcePage = 'photobooth-hire',
}) => {
  const [name, setName]           = useState('');
  const [email, setEmail]         = useState('');
  const [phone, setPhone]         = useState('');
  const [eventDate, setEventDate] = useState(initialEventDate);
  const [venue, setVenue]         = useState('');
  const [eventType, setEventType] = useState('');
  const [packageSlug, setPackageSlug] = useState(initialPackageSlug);
  const [chosenAddons, setChosenAddons] = useState([]);
  const [notes, setNotes]         = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]           = useState(false);
  const [error, setError]         = useState('');

  useEffect(() => { setPackageSlug(initialPackageSlug); }, [initialPackageSlug]);
  useEffect(() => { if (initialEventDate) setEventDate(initialEventDate); }, [initialEventDate]);

  // Lock body scroll while the modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const toggleAddon = (slug) => {
    setChosenAddons((prev) =>
      prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]
    );
  };

  const canSubmit = name.trim() && email.trim() && phone.trim() && eventDate;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError('');
    // iter217 — retry-with-backoff (mirrors the iter216 hardening
    // on /events/draft) so a single Cloudflare blip doesn't drop a
    // booking enquiry. Backend writes to Mongo first, then emails,
    // so a 200 means the enquiry is safely captured even if email
    // delivery hiccups.
    const payload = {
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim(),
      event_date: eventDate,
      venue: venue.trim(),
      event_type: eventType,
      package_slug: packageSlug,
      addons: chosenAddons,
      notes: notes.trim(),
      source_page: sourcePage,
    };
    let lastErr = null;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        await axios.post(`${API}/services/photobooth/enquiry`, payload);
        lastErr = null;
        setDone(true);
        break;
      } catch (err) {
        lastErr = err;
        const status = err?.response?.status;
        if (status && status >= 400 && status < 500) break;
        if (attempt < 2) await new Promise(r => setTimeout(r, 600 * (attempt + 1) ** 2));
      }
    }
    if (lastErr) {
      setError(
        lastErr?.response?.data?.detail ||
        'Your enquiry didn’t land. Please try once more in a moment.'
      );
    }
    setSubmitting(false);
  };

  return (
    <div
      data-testid="enquiry-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(8,5,3,0.72)',
        backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'clamp(16px, 4vw, 40px)',
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          width: '100%', maxWidth: 560,
          background: WALNUT_CARD,
          border: `1px solid ${GOLD_FAINT}`,
          borderRadius: 14,
          padding: 'clamp(22px, 4vw, 36px)',
          boxShadow: '0 30px 90px rgba(0,0,0,0.55)',
          backdropFilter: 'blur(14px)',
          color: IVORY,
        }}
      >
        {done ? (
          <div data-testid="enquiry-success" style={{ textAlign: 'center', padding: '8px 0' }}>
            <div style={{ fontFamily: SERIF, fontSize: 38, color: GOLD, marginBottom: 14 }}>✦</div>
            <h2 style={{
              fontFamily: SERIF, fontWeight: 500, fontSize: 26, margin: 0, color: IVORY,
              letterSpacing: '-0.01em',
            }}>Your enquiry has landed.</h2>
            <p style={{
              fontFamily: SERIF, fontStyle: 'italic', color: IVORY_DIM,
              margin: '14px auto 0', maxWidth: 380, lineHeight: 1.6, fontSize: 15,
            }}>
              We'll confirm availability and reply within one working day with a quote and the next steps.
            </p>
            <button
              type="button"
              onClick={onClose}
              data-testid="enquiry-close"
              style={{ ...PILL_BUTTON_PRIMARY, marginTop: 26 }}
            >
              Close ✦
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} data-testid="enquiry-form">
            <div style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
              color: GOLD, marginBottom: 10, textAlign: 'center',
            }}>✦ ENQUIRE NOW</div>
            <h2 style={{
              fontFamily: SERIF, fontWeight: 500,
              fontSize: 'clamp(24px, 4vw, 30px)', margin: '0 0 6px',
              color: IVORY, textAlign: 'center', letterSpacing: '-0.01em',
            }}>Check availability</h2>
            <p style={{
              fontFamily: SERIF, fontStyle: 'italic', color: IVORY_DIM,
              fontSize: 14, textAlign: 'center', margin: '0 0 22px', lineHeight: 1.55,
            }}>
              Tell us about your evening and we'll confirm availability with a quote.
            </p>

            <div style={{ display: 'grid', gap: 14 }}>
              <div>
                <label style={labelStyle}>Your name</label>
                <input data-testid="enq-name" required value={name} onChange={e => setName(e.target.value)}
                       placeholder="First and last name" style={inputBase} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Email</label>
                  <input data-testid="enq-email" type="email" required value={email}
                         onChange={e => setEmail(e.target.value)} placeholder="you@email.com" style={inputBase} />
                </div>
                <div>
                  <label style={labelStyle}>Phone</label>
                  <input data-testid="enq-phone" type="tel" required value={phone}
                         onChange={e => setPhone(e.target.value)} placeholder="07…" style={inputBase} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Event date</label>
                  <input data-testid="enq-date" type="date" required value={eventDate}
                         onChange={e => setEventDate(e.target.value)} style={{ ...inputBase, colorScheme: 'dark' }} />
                </div>
                <div>
                  <label style={labelStyle}>Event type</label>
                  <select data-testid="enq-event-type" value={eventType}
                          onChange={e => setEventType(e.target.value)} style={{ ...inputBase, colorScheme: 'dark' }}>
                    <option value="">Select…</option>
                    {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={labelStyle}>Venue (if known)</label>
                <input data-testid="enq-venue" value={venue} onChange={e => setVenue(e.target.value)}
                       placeholder="e.g. Tower Hill Barns" style={inputBase} />
              </div>
              {packages.length > 0 && (
                <div>
                  <label style={labelStyle}>Package</label>
                  <select data-testid="enq-package" value={packageSlug}
                          onChange={e => setPackageSlug(e.target.value)} style={{ ...inputBase, colorScheme: 'dark' }}>
                    <option value="">Not sure yet — recommend one</option>
                    {packages.map(p => (
                      <option key={p.slug} value={p.slug}>
                        {p.name} — £{p.price}{p.featured ? ' ⭐' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {addons.length > 0 && (
                <div>
                  <label style={labelStyle}>Add-ons (optional)</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {addons.map(a => {
                      const chosen = chosenAddons.includes(a.slug);
                      return (
                        <button
                          type="button"
                          key={a.slug}
                          data-testid={`enq-addon-${a.slug}`}
                          onClick={() => toggleAddon(a.slug)}
                          style={{
                            padding: '8px 14px', borderRadius: 999,
                            background: chosen ? 'rgba(212,170,82,0.18)' : 'transparent',
                            border: `1px solid ${chosen ? GOLD : GOLD_FAINT}`,
                            color: chosen ? IVORY : IVORY_DIM,
                            fontFamily: SERIF, fontStyle: 'italic',
                            fontSize: 12.5, letterSpacing: '0.02em',
                            cursor: 'pointer',
                            transition: 'all 220ms ease',
                          }}
                        >
                          {a.name}{a.price ? ` — £${a.price}` : ''}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div>
                <label style={labelStyle}>Anything else?</label>
                <textarea
                  data-testid="enq-notes"
                  value={notes} onChange={e => setNotes(e.target.value)}
                  placeholder="Timings, setup details, special requests…"
                  rows={3}
                  style={{ ...inputBase, resize: 'vertical', minHeight: 70 }}
                />
              </div>
            </div>

            {error && (
              <p data-testid="enq-error" style={{
                fontFamily: SERIF, fontStyle: 'italic',
                fontSize: 13, color: '#FFAA90',
                margin: '14px 0 0', textAlign: 'center',
              }}>{error}</p>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
              <button
                type="button"
                onClick={onClose}
                data-testid="enq-cancel"
                style={{
                  flex: '0 0 auto',
                  padding: '14px 22px', borderRadius: 999,
                  background: 'transparent',
                  color: IVORY_DIM,
                  border: `1px solid ${GOLD_FAINT}`,
                  fontFamily: SERIF, fontStyle: 'italic', fontSize: 14,
                  letterSpacing: '0.04em', cursor: 'pointer',
                }}
              >Cancel</button>
              <button
                type="submit"
                disabled={!canSubmit || submitting}
                data-testid="enq-submit"
                style={{
                  ...PILL_BUTTON_PRIMARY,
                  flex: 1,
                  opacity: (!canSubmit || submitting) ? 0.55 : 1,
                  cursor: (!canSubmit || submitting) ? 'not-allowed' : 'pointer',
                }}
              >
                {submitting ? 'Sending…' : 'Send Enquiry ✦'}
              </button>
            </div>

            <p style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 11.5, color: IVORY_GHOST, textAlign: 'center',
              margin: '14px 0 0', letterSpacing: '0.02em',
            }}>
              We confirm availability before any deposit is taken. No subscription, no surprise charges.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default EnquiryForm;
